import {Action, openDialog} from 'platform/components';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetVehicleQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {customerRoutes, vehiclesRoutes} from '@dms/routes';

import {buildArray, composePath, DOT_CHARACTER, Nullish} from 'shared/web';

import {getCustomerEmail} from '../../../utils/getCustomerEmail';
import {getCustomerName} from '../../../utils/getCustomerName';
import {getCustomerPhoneNumber} from '../../../utils/getCustomerPhoneNumber';
import {isCustomerNaturalPerson} from '../../../utils/isCustomerNaturalPerson';
import {SendCustomerEmailForm} from '../../SendCustomerEmailForm/SendCustomerEmailForm';
import {SendCustomerSmsForm} from '../../SendCustomerSmsForm/SendCustomerSmsForm';
import {TireSetDetailFormDefinition} from '../types/TireSetDetailFormDefinition';

type ContactInformation = TireSetDetailFormDefinition['contactInformation'];
type ContactPhoneNumber = NonNullable<NonNullable<ContactInformation>['phoneNumber']>;
const normalizeString = (value: string | Nullish) => value?.trim().toLowerCase() ?? '';

const getContactPersonPhone = (phoneNumber: ContactPhoneNumber | Nullish) => {
  if (isNilOrEmpty(phoneNumber?.number)) {
    return null;
  }

  if (isNilOrEmpty(phoneNumber?.prefix)) {
    return phoneNumber?.number;
  }

  return `${phoneNumber?.prefix} ${phoneNumber?.number}`;
};

interface UseTireSetControlsProps {
  vehicleId: string;
  customerId: string;
  contactInformation: TireSetDetailFormDefinition['contactInformation'];
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function useTireSetControls(props: UseTireSetControlsProps) {
  const {data: vehicleData} = useGetVehicleQuery(
    {vehicleId: props.vehicleId},
    {skip: isNilOrEmpty(props.vehicleId)}
  );

  const {data: customerData} = useGetCustomerV2Query(
    {customerId: props.customerId},
    {skip: isNilOrEmpty(props.customerId)}
  );
  const selectedCustomerId = props.contactInformation?.id;
  const {data: selectedCustomerData} = useGetCustomerV2Query(
    {customerId: selectedCustomerId ?? ''},
    {skip: isNilOrEmpty(selectedCustomerId)}
  );

  const handleVehicleClick = () => {
    window.open(composePath(vehiclesRoutes.detail, {params: {id: props.vehicleId}}))?.focus();
  };

  const handleCustomerClick = () => {
    window.open(composePath(customerRoutes.detail, {params: {id: props.customerId}}))?.focus();
  };
  const handleSelectedCustomerClick = () => {
    if (isNilOrEmpty(selectedCustomerId)) {
      return;
    }

    window.open(composePath(customerRoutes.detail, {params: {id: selectedCustomerId}}))?.focus();
  };

  const handleEmailClick = () => {
    const recordId = selectedCustomerId ?? props.customerId;
    openDialog(
      <SendCustomerEmailForm
        customerId={recordId}
        resourceId={EntityResourceIds.customer}
        recordId={recordId}
      />,
      {
        id: 'tireSetSendCustomerEmail',
        withAdditionalFooter: true,
        size: 'large',
        title: i18n.t('general.actions.sendEmail'),
      }
    );
  };

  const handlePhoneNumberClick = () => {
    const recordId = selectedCustomerId ?? props.customerId;
    openDialog(
      <SendCustomerSmsForm
        customerId={recordId}
        recordId={recordId}
        resourceId={EntityResourceIds.customer}
      />,
      {
        id: 'tireSetSendCustomerSms',
        scrollBehavior: 'outside',
        size: 'large',
        title: i18n.t('general.labels.smsMessage'),
      }
    );
  };

  if (isNilOrEmpty(customerData)) {
    return [];
  }

  const companyName = getCustomerName(selectedCustomerData);
  const originalOwnerName = getCustomerName(customerData);
  const originalOwnerIcon = isCustomerNaturalPerson(customerData)
    ? 'social/person'
    : 'social/domain';
  const originalOwnerEmail = getCustomerEmail(customerData);
  const originalOwnerPhone = getCustomerPhoneNumber(customerData);
  const contactPersonName = props.contactInformation?.name;
  const contactPersonEmail = props.contactInformation?.email;
  const contactPersonPhone = getContactPersonPhone(props.contactInformation?.phoneNumber);
  const shouldShowCompany =
    !isNilOrEmpty(companyName) &&
    !!selectedCustomerData &&
    !isCustomerNaturalPerson(selectedCustomerData) &&
    normalizeString(companyName) !== normalizeString(originalOwnerName);
  const selectedNaturalPersonName =
    !!selectedCustomerData && isCustomerNaturalPerson(selectedCustomerData) ? companyName : null;
  const selectedContactName = contactPersonName ?? selectedNaturalPersonName;
  const shouldShowContactPersonData =
    !isNilOrEmpty(selectedContactName) &&
    normalizeString(selectedContactName) !== normalizeString(originalOwnerName) &&
    (!shouldShowCompany || normalizeString(selectedContactName) !== normalizeString(companyName));
  const shouldShowContactPersonDetails =
    shouldShowContactPersonData &&
    (!isNilOrEmpty(contactPersonEmail) || !isNilOrEmpty(contactPersonPhone));
  const shouldShowSelectedBlock = shouldShowCompany || shouldShowContactPersonData;
  const hasSelectedContactName = !isNilOrEmpty(selectedContactName);
  const hasContactPersonPhone = shouldShowContactPersonDetails && !isNilOrEmpty(contactPersonPhone);
  const hasContactPersonEmail = shouldShowContactPersonDetails && !isNilOrEmpty(contactPersonEmail);

  const dotAction: Action = {
    type: 'custom',
    component: <>{DOT_CHARACTER}</>,
  };

  const actions = buildArray<Action>()
    .whenNot(isNilOrEmpty(vehicleData), {
      type: 'button',
      leftIcon: 'maps/directions_car',
      variant: 'link',
      title: [vehicleData?.title, vehicleData?.registrationPlate, vehicleData?.vin]
        .filter(Boolean)
        .join(` ${DOT_CHARACTER} `),
      onClick: handleVehicleClick,
    })
    .whenNot(isNilOrEmpty(vehicleData), dotAction)
    .add({
      type: 'button',
      leftIcon: originalOwnerIcon,
      variant: 'link',
      title: `${originalOwnerName}`,
      onClick: handleCustomerClick,
    })
    .when(shouldShowCompany, dotAction, {
      type: 'button',
      leftIcon: 'social/domain',
      variant: 'link',
      title: `${companyName}`,
      onClick: handleSelectedCustomerClick,
    })
    .when(shouldShowContactPersonData, dotAction)
    .when(shouldShowContactPersonData && hasSelectedContactName, {
      type: 'button',
      leftIcon: 'social/person',
      variant: 'link',
      title: selectedContactName ?? '',
      onClick: handleSelectedCustomerClick,
    })
    .when(hasContactPersonPhone && hasSelectedContactName, dotAction)
    .when(hasContactPersonPhone, {
      type: 'button',
      variant: 'link',
      title: contactPersonPhone ?? '',
      onClick: handlePhoneNumberClick,
    })
    .when(!shouldShowSelectedBlock && !isNilOrEmpty(originalOwnerPhone), dotAction, {
      type: 'button',
      variant: 'link',
      title: originalOwnerPhone ?? '',
      onClick: handlePhoneNumberClick,
    })
    .when(hasContactPersonEmail && (hasSelectedContactName || hasContactPersonPhone), dotAction)
    .when(hasContactPersonEmail, {
      type: 'button',
      variant: 'link',
      title: contactPersonEmail ?? '',
      onClick: handleEmailClick,
    })
    .when(!shouldShowSelectedBlock && !isNilOrEmpty(originalOwnerEmail), dotAction, {
      type: 'button',
      variant: 'link',
      title: originalOwnerEmail ?? '',
      onClick: handleEmailClick,
    });

  return actions;
}
